import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const POLL_MS = 60000;
const LATEST = 8;

const DOT = {
  error: 'bg-red-500',
  warning: 'bg-orange-500',
  success: 'bg-green-500',
};

/**
 * OFF-25: header bell. The unread count of the signed-in user's own
 * notifications (polled every minute and when the window gets focus), the
 * latest ones in a dropdown (rendered in the reader's language by the
 * backend), mark one / all as read, and a link to the full list.
 * P2-01: company announcements addressed to the user arrive here too
 * (kind "announcement", unread until opened or marked).
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const ref = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`);
      setCount(res.data?.count || 0);
    } catch (e) {
      // transient: next tick
    }
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, { params: { page: 1, page_size: LATEST } });
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_MS);
    const onFocus = () => refreshCount();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open) {
      setItems(null);
      loadLatest();
      refreshCount();
    }
    setOpen(!open);
  };

  const markRead = async (n) => {
    if (n.read) return;
    setItems((list) => (list || []).map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setCount((c) => Math.max(0, c - 1));
    try {
      await axios.post(`${API_URL}/api/notifications/${n.id}/read`);
    } catch (e) {
      refreshCount();
    }
  };

  const openItem = (n) => {
    markRead(n);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    setItems((list) => (list || []).map((x) => ({ ...x, read: true })));
    setCount(0);
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`);
    } catch (e) {
      refreshCount();
    }
  };

  const label = count > 0 ? t('shell.notifications.unreadLabel', { n: String(count) }) : t('shell.notifications.title');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        title={label}
        aria-expanded={open}
        data-testid="notification-bell"
        className="relative p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -end-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-[1.1rem] text-center"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('shell.notifications.title')}
          className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:end-0 sm:mt-2 sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('shell.notifications.title')}</span>
            {count > 0 && (
              <button type="button" onClick={markAll} className="text-xs text-primary-600 hover:underline" data-testid="notification-mark-all">
                {t('shell.notifications.markAllRead')}
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items === null && (
              <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            )}
            {items && items.length === 0 && (
              <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t('shell.notifications.empty')}</p>
            )}
            {items && items.map((n) => (
              <div
                key={n.id}
                className={`flex items-start gap-2 px-4 py-3 border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${n.read ? '' : 'bg-primary-50/60 dark:bg-gray-700/40'}`}
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${n.read ? 'bg-transparent' : (DOT[n.type] || 'bg-blue-500')}`} />
                <button type="button" onClick={() => openItem(n)} className="flex-1 min-w-0 text-start">
                  {n.kind === 'announcement' && (
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 rounded px-1.5 py-0.5 mb-1" data-testid="notification-announcement">
                      {t('shell.notifications.announcement')}
                    </span>
                  )}
                  <span className={`block text-sm text-gray-900 dark:text-gray-100 ${n.read ? '' : 'font-semibold'}`}>{n.title}</span>
                  {n.message && <span className="block text-xs text-gray-600 dark:text-gray-300 mt-0.5 break-words whitespace-pre-line line-clamp-4">{n.message}</span>}
                  {n.created_at && <span className="block text-[11px] text-gray-400 mt-1">{formatDateTime(n.created_at)}</span>}
                </button>
                {!n.read && (
                  <button
                    type="button"
                    onClick={() => markRead(n)}
                    title={t('shell.notifications.markRead')}
                    aria-label={t('shell.notifications.markRead')}
                    className="text-gray-400 hover:text-primary-600 text-xs flex-shrink-0"
                  >
                    ✓
                  </button>
                )}
              </div>
            ))}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-sm text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 px-4 py-2 border-t border-gray-200 dark:border-gray-700 rounded-b-lg"
          >
            {t('shell.notifications.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}
